import { ToRegistrantGroupTypeService } from '@reg/shared/service/to-registrant-group-type.service';
import { EmailRecipient } from '@reg/shared/model';
import { convertToDateTime } from '../helper/dateTime-helper';

/**
 * Model to hold a single email message.
 */
export class EmailMessage {
    public id: string = null;
    public eventId: string = null;
    public fromName: string = null;
    public fromEmail: string = null;
    public subject: string = null;
    public body: string = null;
    public sentDateTime: Date = null;
    public toRecipientGroup: string = null;
    public toRecipients: EmailRecipient[] = [];
    public eventTimeZone: string = null;
    public toRecipientGroupName: string = null;

    public static getFields(isForList: boolean): string[] {
        let fields: string[] = [
            'eventId',
            'fromName',
            'fromEmail',
            'subject',
            'sentDateTime',
            'toRecipientGroup',
            'eventTimeZone',
        ];

        if (!isForList) {
            fields = [].concat(['body', 'toRecipients'], fields);
        }

        return fields;
    }

    constructor() {
        this.toRecipientGroup = ToRegistrantGroupTypeService.EVERYONE;
    }

    public clone(): EmailMessage {
        const clone: EmailMessage = new EmailMessage();
        clone.id = this.id;
        clone.fromName = this.fromName;
        clone.fromEmail = this.fromEmail;
        clone.subject = this.subject;
        clone.body = this.body;
        clone.toRecipientGroup = this.toRecipientGroup;
        clone.eventTimeZone = this.eventTimeZone;

        // NA (Custom) is no longer allowed so remove it so the user will be forced to select another group
        if (clone.toRecipientGroup === ToRegistrantGroupTypeService.NA) {
            clone.toRecipientGroup = null;
        }

        if (this.sentDateTime) {
            clone.sentDateTime = new Date(this.sentDateTime.getTime());
        }

        if (this.toRecipients) {
            clone.toRecipients = this.toRecipients.map((x: EmailRecipient) => x.clone());
        }

        return clone;
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'emailMessage';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.fromName = this.fromName;
        json.data.attributes.fromEmail = this.fromEmail;
        json.data.attributes.subject = this.subject;
        json.data.attributes.body = this.body;
        json.data.attributes.toRecipientGroup = this.toRecipientGroup;

        return JSON.stringify(json);
    }

    /**
     * Deserialize to a json api format
     */
    public deserialize(json: any, included?: any[]): EmailMessage {
        const data: any = json['data'] || json;
        const attrs: any = data['attributes'];
        const relationships: any[] = data['relationships'] || [];
        included = included || json.included;

        this.id = data['id'];
        this.fromName = attrs['fromName'];
        this.fromEmail = attrs['fromEmail'];
        this.subject = attrs['subject'];
        this.body = attrs['body'];
        this.toRecipientGroup = attrs['toRecipientGroup'];
        this.eventId = attrs['eventId'];
        this.eventTimeZone = attrs['eventTimeZone'];

        if (attrs['sentDateTime']) {
            this.sentDateTime = convertToDateTime(attrs['sentDateTime']);
        }

        // Fill toRecipients from relationships if it exists
        const recipientLinks: any = relationships['toRecipients'];

        if (recipientLinks &&
            included &&
            recipientLinks['data']) {
            this.toRecipients = [];

            for (const recipientLink of recipientLinks['data']) {
                const recipient: any = included.filter((i: any) => i['id'] === recipientLink['id'])[0];
                if (recipient) {
                    this.toRecipients.push((new EmailRecipient()).deserialize(recipient));
                }
            }
        }

        return this;
    }
}
